import { useMemo } from "react";
import { GoalCategoryTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

type GoalCategoryStrings = {
	defaultName: string;
	sectionTitle: string;
	amountLabel: string;
	amountHint: string | undefined;
	ownFundsLabel: string;
	loanLabel: string;
	loanHint: string | undefined;
	remainingLabel: string;
};

export const useGoalCategoryStrings = (
	category: GoalCategoryTypeEnum | undefined,
): GoalCategoryStrings => {
	const { t } = useResource();

	const defaultNameByCategory = useMemo(
		(): Record<GoalCategoryTypeEnum, string> => ({
			[GoalCategoryTypeEnum.House]: t(
				Resources.Goals.Form.DefaultNames.Housing,
			),
			[GoalCategoryTypeEnum.Car]: t(Resources.Goals.Form.DefaultNames.Car),
			[GoalCategoryTypeEnum.Pension]: t(
				Resources.Goals.Form.DefaultNames.Retirement,
			),
			[GoalCategoryTypeEnum.Rent]: t(Resources.Goals.Form.DefaultNames.Rent),
			[GoalCategoryTypeEnum.Student]: t(
				Resources.Goals.Form.DefaultNames.Education,
			),
			[GoalCategoryTypeEnum.Vacation]: t(
				Resources.Goals.Form.DefaultNames.Vacation,
			),
			[GoalCategoryTypeEnum.Basic]: "",
		}),
		[t],
	);

	const sectionTitleByCategory = useMemo(
		(): Partial<Record<GoalCategoryTypeEnum, string>> => ({
			[GoalCategoryTypeEnum.House]: t(
				Resources.Goals.Form.SectionTitles.Housing,
			),
			[GoalCategoryTypeEnum.Car]: t(Resources.Goals.Form.SectionTitles.Car),
			[GoalCategoryTypeEnum.Pension]: t(
				Resources.Goals.Form.SectionTitles.Retirement,
			),
			[GoalCategoryTypeEnum.Rent]: t(
				Resources.Goals.Form.SectionTitles.Retirement,
			),
			[GoalCategoryTypeEnum.Student]: t(
				Resources.Goals.Form.SectionTitles.Education,
			),
			[GoalCategoryTypeEnum.Vacation]: t(
				Resources.Goals.Form.SectionTitles.Vacation,
			),
			[GoalCategoryTypeEnum.Basic]: t(
				Resources.Goals.Form.SectionTitles.Default,
			),
		}),
		[t],
	);

	const amountLabelByCategory = useMemo(
		(): Record<GoalCategoryTypeEnum, string> => ({
			[GoalCategoryTypeEnum.House]: t(
				Resources.Goals.Form.AmountLabels.Housing,
			),
			[GoalCategoryTypeEnum.Car]: t(Resources.Goals.Form.AmountLabels.Car),
			[GoalCategoryTypeEnum.Pension]: t(
				Resources.Goals.Form.AmountLabels.Retirement,
			),
			[GoalCategoryTypeEnum.Rent]: t(
				Resources.Goals.Form.AmountLabels.Retirement,
			),
			[GoalCategoryTypeEnum.Student]: t(
				Resources.Goals.Form.AmountLabels.Education,
			),
			[GoalCategoryTypeEnum.Vacation]: t(
				Resources.Goals.Form.AmountLabels.Vacation,
			),
			[GoalCategoryTypeEnum.Basic]: t(
				Resources.Goals.Form.AmountLabels.Default,
			),
		}),
		[t],
	);

	const amountHintByCategory = useMemo(
		(): Partial<Record<GoalCategoryTypeEnum, string>> => ({
			[GoalCategoryTypeEnum.Pension]: t(
				Resources.Goals.Form.AmountHints.Retirement,
			),
		}),
		[t],
	);

	const ownFundsLabelByCategory = useMemo(
		(): Partial<Record<GoalCategoryTypeEnum, string>> => ({
			[GoalCategoryTypeEnum.House]: t(
				Resources.Goals.Form.OwnFundsLabels.Housing,
			),
			[GoalCategoryTypeEnum.Car]: t(Resources.Goals.Form.OwnFundsLabels.Car),
			[GoalCategoryTypeEnum.Student]: t(
				Resources.Goals.Form.OwnFundsLabels.Education,
			),
			[GoalCategoryTypeEnum.Vacation]: t(
				Resources.Goals.Form.OwnFundsLabels.Vacation,
			),
			[GoalCategoryTypeEnum.Basic]: t(
				Resources.Goals.Form.OwnFundsLabels.Default,
			),
		}),
		[t],
	);

	const loanLabelByCategory = useMemo(
		(): Partial<Record<GoalCategoryTypeEnum, string>> => ({
			[GoalCategoryTypeEnum.House]: t(Resources.Goals.Form.LoanLabels.Housing),
			[GoalCategoryTypeEnum.Car]: t(Resources.Goals.Form.LoanLabels.Car),
			[GoalCategoryTypeEnum.Basic]: t(Resources.Goals.Form.LoanLabels.Default),
		}),
		[t],
	);

	const loanHintByCategory = useMemo(
		(): Partial<Record<GoalCategoryTypeEnum, string>> => ({
			[GoalCategoryTypeEnum.House]: t(Resources.Goals.Form.LoanHints.Housing),
			[GoalCategoryTypeEnum.Car]: t(Resources.Goals.Form.LoanHints.Car),
			[GoalCategoryTypeEnum.Basic]: t(Resources.Goals.Form.LoanHints.Default),
		}),
		[t],
	);

	const usesDefaultRemainingLabel =
		!category || category === GoalCategoryTypeEnum.Basic;
	const defaultName = category ? (defaultNameByCategory[category] ?? "") : "";
	const sectionTitle = category
		? (sectionTitleByCategory[category] ?? "")
		: t(Resources.Goals.Form.sectionAmount);
	const amountLabel = category
		? (amountLabelByCategory[category] ?? "")
		: t(Resources.Goals.Form.AmountLabels.Default);
	const amountHint = category ? amountHintByCategory[category] : undefined;
	const ownFundsLabel = category
		? (ownFundsLabelByCategory[category] ?? "")
		: t(Resources.Goals.Form.OwnFundsLabels.Default);
	const loanLabel = category
		? (loanLabelByCategory[category] ??
			t(Resources.Goals.Form.LoanLabels.Default))
		: t(Resources.Goals.Form.LoanLabels.Default);
	const loanHint = category ? loanHintByCategory[category] : undefined;
	const remainingLabel = usesDefaultRemainingLabel
		? t(Resources.Goals.Form.RemainingLabels.Default)
		: t(Resources.Goals.Form.RemainingLabels.Saving);

	return {
		defaultName,
		sectionTitle,
		amountLabel,
		amountHint,
		ownFundsLabel,
		loanLabel,
		loanHint,
		remainingLabel,
	};
};
